import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, RefreshCw, Check, Pencil, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATE_STYLES = {
  awaiting_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  no_quiz: 'bg-gray-100 text-gray-600',
};

const STATE_LABELS = {
  awaiting_review: 'AI draft — needs review',
  approved: 'Approved',
  no_quiz: 'No questions',
};

/** Per-pillar review queue for AI-drafted module knowledge checks. */
export const QuizReviewQueue = ({ pillars = [], onEditModule, reviewedBy = 'system_admin' }) => {
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showApproved, setShowApproved] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pillars/admin/quiz-review-queue`);
      if (response.ok) setQueue(await response.json());
    } catch (error) {
      console.error('Error loading quiz review queue:', error);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (pillar, module, action) => {
    const key = `${pillar.pillar_id}:${module.sub_pillar_id}:${action}`;
    setBusy(key);
    try {
      const path =
        action === 'approve'
          ? `/api/pillars/admin/quiz-approve/${pillar.pillar_id}/${encodeURIComponent(module.sub_pillar_id)}`
          : `/api/pillars/admin/generate-quiz/${pillar.pillar_id}/${encodeURIComponent(module.sub_pillar_id)}`;
      const response = await fetch(`${BACKEND_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: action === 'approve' ? JSON.stringify({ reviewed_by: reviewedBy }) : undefined,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Request failed');
      await load();
    } catch (error) {
      alert(`Could not ${action} this module's quiz: ${error.message}`);
    } finally {
      setBusy(null);
    }
  };

  const openEditor = (pillarSummary, module) => {
    const fullPillar = pillars.find((p) => p.pillar_id === pillarSummary.pillar_id);
    const fullModule = fullPillar?.sub_pillars?.find((sp) => sp.sub_pillar_id === module.sub_pillar_id);
    if (fullPillar && fullModule && onEditModule) onEditModule(fullPillar, fullModule);
    else alert('Open this module from the pillar below to edit its questions.');
  };

  if (!queue) return null;

  const { totals } = queue;

  return (
    <div className="bg-white rounded-2xl shadow-xl mb-8 border border-gray-100" data-testid="quiz-review-queue">
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 rounded-lg p-2">
            <ClipboardCheck className="h-5 w-5 text-amber-700" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Knowledge-Check Review</h2>
            <p className="text-sm text-gray-500">
              AI-drafted questions stay flagged until you approve them
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm font-semibold">
          <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800" data-testid="quiz-review-awaiting-count">
            {totals.awaiting_review} awaiting review
          </span>
          <span className="px-3 py-1 rounded-full bg-green-100 text-green-800">{totals.approved} approved</span>
          <button
            onClick={() => setShowApproved((v) => !v)}
            data-testid="quiz-review-toggle-approved"
            className="px-3 py-1 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            {showApproved ? 'Hide approved' : 'Show all'}
          </button>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {queue.pillars.map((pillar) => {
          const modules = pillar.modules.filter((m) => showApproved || m.review_state !== 'approved');
          if (!modules.length) return null;
          return (
            <div key={pillar.pillar_id} className="px-6 py-4">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2 py-1 rounded-md bg-indigo-100 text-indigo-800 text-xs font-bold">
                  Pillar {pillar.pillar_number}
                </span>
                <h3 className="font-bold text-gray-900">{pillar.name}</h3>
                {pillar.awaiting_review > 0 && (
                  <span className="text-xs font-semibold text-amber-700">
                    {pillar.awaiting_review} to review
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {modules.map((module) => (
                  <div
                    key={module.sub_pillar_id}
                    data-testid={`quiz-review-row-${module.sub_pillar_id}`}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{module.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATE_STYLES[module.review_state]}`}>
                          {STATE_LABELS[module.review_state]}
                        </span>
                        <span className="text-xs text-gray-500">{module.question_count} questions</span>
                        {module.reviewed_by && module.review_state === 'approved' && (
                          <span className="text-xs text-gray-400">by {module.reviewed_by}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditor(pillar, module)}
                        data-testid={`quiz-review-edit-${module.sub_pillar_id}`}
                        className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-white flex items-center gap-1"
                      >
                        <Pencil className="h-4 w-4" /> Edit
                      </button>
                      <button
                        onClick={() => act(pillar, module, 'regenerate')}
                        disabled={busy?.startsWith(`${pillar.pillar_id}:${module.sub_pillar_id}`)}
                        data-testid={`quiz-review-regenerate-${module.sub_pillar_id}`}
                        className="px-3 py-2 rounded-lg border border-indigo-300 text-indigo-700 text-sm font-semibold hover:bg-indigo-50 flex items-center gap-1 disabled:opacity-50"
                      >
                        <RefreshCw className="h-4 w-4" /> Regenerate
                      </button>
                      <button
                        onClick={() => act(pillar, module, 'approve')}
                        disabled={module.review_state === 'no_quiz' || busy?.startsWith(`${pillar.pillar_id}:${module.sub_pillar_id}`)}
                        data-testid={`quiz-review-approve-${module.sub_pillar_id}`}
                        className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 flex items-center gap-1 disabled:opacity-40"
                      >
                        <Check className="h-4 w-4" /> Approve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {totals.awaiting_review === 0 && !showApproved && (
          <div className="px-6 py-6 flex items-center gap-3 text-gray-600">
            <AlertCircle className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium">
              Every module's knowledge check has been reviewed and approved.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizReviewQueue;
